import {
  D3GradientData,
  D3LineData,
  D3PathFilledData,
  d3renderCircle,
  d3renderGradient,
  d3renderLine,
  d3renderPath,
  d3renderPathFilled,
  d3renderRect,
  d3renderText,
  GradientStopData,
} from '../chart.d3';
import { ChartManager, DataRowExt } from '../chart.manager';

import { cloneObject, roundTo } from '@23degrees/shared-code/utilities';

import { DataImage } from '@23degrees/shared-code/domain/content/entities/components/chart/DataImage';
import { DataLabel } from '@23degrees/shared-code/domain/content/entities/components/chart/DataLabel';
import * as Markers from '@23degrees/packages/map-overlays2/components/marker-icon-selector2/markers.constants';

import { getOldFields } from '@23degrees/shared-code/config/dimension.config';
import chroma from 'chroma-js';
import { select } from 'd3-selection';
import { overlayText } from '../chart.overlay';
import { CustomDots, DotRanges } from '@23degrees/shared-code/models';
import { capitalize, isIterable, toCamelCase } from '@23degrees/shared-code/utilities/format';
import { color2css, createArrow, fixLineHeight, strokeDasharrayFormat } from '../utilities/utils';
import { isArray, isNumber } from 'std-kit';
import { convertDeltaToHTML } from '@23degrees/quill';

interface DataLabelInfo {
  id: string;
  value: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export const renderPlot = (chartManager: ChartManager): void => {
  const { type, typeSpecific } = chartManager.content;
  const { labelField, valueField, aggregationField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);
  const { animation, values, dots, dynamicHeight } = typeSpecific.chart;
  const { labelAxis } = typeSpecific.chart.axis;
  const { backgroundColor } = typeSpecific.style.background;
  const { colors2 } = typeSpecific.style;

  const dynamicStyle = cloneObject(values.style);
  dynamicStyle.size = chartManager.getDynamicFontSize(values.style);

  const dynamicValueStyle = cloneObject(labelAxis.valueStyle);
  dynamicValueStyle.size = chartManager.getDynamicFontSize(labelAxis.valueStyle);

  const oneLineHeight = dynamicValueStyle.size + dynamicValueStyle.size * (fixLineHeight(dynamicValueStyle.lineheight.value) - 1);
  const valueMarginTop = chartManager.getStyleInPx(dynamicValueStyle.margin, 'top');
  const valueMarginBottom = chartManager.getStyleInPx(dynamicValueStyle.margin, 'bottom');
  const valueMarginLeft = chartManager.getStyleInPx(dynamicValueStyle.margin, 'left');
  const valueMarginRight = chartManager.getStyleInPx(dynamicValueStyle.margin, 'right');
  const valueHeight = oneLineHeight + valueMarginTop + valueMarginBottom;

  const isGroupColor = ['continuous', 'gradient', 'range'].includes(colors2.type);

  const dataLabel = DataLabel.create(values);
  const dataImage = DataImage.create(values);

  const data: D3PathFilledData[] = [];
  const rangeData: D3LineData[] = [];
  const gradientData: D3GradientData[] = [];
  const textData = [];
  const overlayData = [];

  const labelData: Record<string, Record<string, DataLabelInfo>> = {}; // Store x,y,height,width for better manipulation

  const colorMap: any = {};
  const groupRecord: Record<string, DataRowExt[]> = {};    
    
  const coloringField: string = colors2.categorical.fieldName || labelField
  const sameColor = aggregationField === coloringField
  
  for (const [stackIdx, stack] of [...chartManager.dataPlot].entries()) {
    for (const [valueIdx, obj] of stack.values.entries()) {
      const { value, actualObj } = obj;
      const group = actualObj[aggregationField] as string;
      if (!groupRecord[group]) groupRecord[group] = [];
      groupRecord[group].push(actualObj);

      const dataGroup = chartManager.dataGroup.find((groupObj) => groupObj[0] === obj.key);
      const color = isGroupColor ? chartManager.groupedColorScale(dataGroup['~meta']) : chartManager.colorScale(actualObj);
      colorMap[actualObj[coloringField] as string] = color;
    }
  }

  for (const group of Object.values(groupRecord)) {
    group.sort((a, b) => (b[valueField] as number) - (a[valueField] as number)).reverse();
  }

  for (const [stackIdx, stack] of [...chartManager.dataPlot].entries()) {
    for (const [valueIdx, obj] of stack.values.entries()) {
      const { value, actualObj } = obj;

      let awayFromZero = false; // Default
      let arrowGroup = false; // Default
      let dotRange: DotRanges | null = null;

      // Is this group on a directional range ?
      if (dots.ranges) {
        dotRange = dots.ranges.find((range: DotRanges) => range.from === obj.key || range.to === obj.key);
        if (dotRange?.directional) {
          if (dotRange.from === '__zero') awayFromZero = 0 <= stack[dotRange.to];
          else if (dotRange.to === '__zero') awayFromZero = stack[dotRange.from] <= 0;
          else awayFromZero = stack[dotRange.from] <= stack[dotRange.to];
          arrowGroup = dotRange.to === obj.key;
        }
      }

      const group = chartManager.dataGroup.find((groupObj) => groupObj[0] === obj.key);

      let color = isGroupColor ? chartManager.groupedColorScale(group['~meta']) : chartManager.colorScale(actualObj);
      const hoverColor = color2css(chroma(color).darken());

      const aggregationColor = colorMap[actualObj[coloringField] as string]

      const isNegative = value < 0;
      const aggregation = obj.group;

      const rangeAdjust = chartManager.rangeMin >= 0 ? Math.min(chartManager.rangeMin, value) : Math.min(0, value);
      let x = chartManager.xScale(rangeAdjust);
      const width = chartManager.horizontalWidth(value > 0 ? value - rangeAdjust : value);

      // let y = chartManager.yScale(aggregation);
      // let height = chartManager.horizontalHeight(type, aggregation);

      // yScaleOrdinal
      const { item } = chartManager.yScaleOrdinal(aggregation);
      const y = item.y;
      const height = item.height;

      const click = (event) => {
        chartManager.openPopup(event, actualObj);
        chartManager.mouseClick(event, actualObj);
      };

      const enter = (event, _, node) => {
        select(node).attr('fill', hoverColor);
        chartManager.mouseEnter(event, actualObj);
      };

      const leave = (event, _, node) => {
        select(node).attr('fill', color);
        chartManager.mouseLeave(event, actualObj);
      };

      const move = (event, _, node) => chartManager.mouseMove(event, actualObj);

      const callbacks = { click, enter, leave, move };

      // Symbol
      const customDots = dots.customDots.find((item: CustomDots) => item.id === obj.key);
      const symbol = customDots ? customDots.symbol : dots.symbol;
      // const labelPosition = customDots ? customDots.labelPosition : dots.labelPosition;
      const labelPosition = dots.labelPosition; // Only global
      let size = customDots ? customDots.size : dots.size;
      let scaleX = size / 15; // 15 is the size of svg path
      let scaleY = size / 15; // 15 is the size of svg path

      let path = '';
      let name = '';
      if (symbol) {
        name = `marker${capitalize(toCamelCase(symbol))}`;
        const svg = Markers[name].data;

        const pathMatch = svg.match(/\sd="([^"]+)"/);
        if (pathMatch?.[1]) {
          path = pathMatch[1];
        }
      }

      const { dataLabelStr, dataLabelWidth, dataLabelHeight } = actualObj['~meta'];

      let dotX = isNegative ? x - size / 2 : x + width - size / 2;
      let doty = roundTo(y + height / 2 - size / 2);

      // In case of directional Group (arrow), replace symbol with icon                        
      if (arrowGroup) {
        const { strokeWidth, stroke, colorMode } = dotRange;
        switch (colorMode) {
          case 'source':
            color = sameColor ? aggregationColor : colorMap[dotRange.from];
            break;
          case 'target':
            color = sameColor ? aggregationColor : colorMap[dotRange.to];
            break;
          case 'gradient':
            // color = `url(#gradient-${directionalRange.id}-${stackIdx})`;
            color = sameColor ? aggregationColor : colorMap[dotRange.to];
            break;
          default:
            color = stroke;
            break;
        }

        const arrowSize = strokeWidth * 5;

        doty = roundTo(y + height / 2);
        dotX = isNegative ? x - arrowSize : x + width - arrowSize;
        scaleX = 1;
        scaleY = 1;
        size = arrowSize;
        path = createArrow(strokeWidth, arrowSize);

        if (!awayFromZero) {
          scaleX = -1;
          dotX = isNegative ? x + arrowSize : x + width + arrowSize;
        }
      }

      const transform = [`translate(${dotX},${doty})`, `scale(${scaleX},${scaleY})`];

      const animate = {
        enabled: animation.enabled,
        delay: animation.delay,
        duration: animation.duration,
        x: roundTo(x),
        y: roundTo(y),
        path: path,
        // width: roundTo(width),
        // height: roundTo(height),
        color: color2css(color),
        transform: transform.join(','),
      };

      data.push({
        x: roundTo(x),
        y: roundTo(y),
        // width: 0,
        // height: roundTo(height),
        path: path,
        color: color2css(color),
        transform: `translate(${chartManager.xScale(0)},${doty})`,
        // transform: transform.join(','),
        animate,
        callbacks,
        crispEdges: arrowGroup,
      });

      if (!values.enabled) continue;

      // ---- Data Labels

      const actualSize = undefined; // chartManager.getElementRect(dataLabelStr, chartManager.textRotate ? height : width);

      const availableWidth = chartManager.horizontalSpace(value);

      // callbacks
      const clickQ = (event, _, node) => {};
      const enterQ = (event, _, node) => {};
      const leaveQ = (event, _, node) => {};
      const moveQ = (event, _, node) => {};

      const callbacksQ = { click: clickQ, leave: leaveQ, enter: enterQ, move: moveQ };

      // Recalculate position based on params, symbol size and label size
      // Arrow

      const xLabel = x;
      let xWidth = width;
      let yLabel = y;

      switch (labelPosition) {
        case 'bottom':
        case 'spread-bottom':
          yLabel = y + size / 2 + dataLabelHeight / 2;
          break;

        // case 'top':
        // case 'spread-top':
        default:
          yLabel = y - size / 2 - dataLabelHeight / 2;
          break;
      }

      if (isNegative) {
        x -= dataLabelWidth / 2;
      }
      xWidth += dataLabelWidth / 2;

      if (['spread-top', 'spread-bottom'].includes(labelPosition)) {
        if (groupRecord[aggregation].length > 1) {
          const idx = groupRecord[aggregation].findIndex((item: any) => item['~meta'].id === actualObj['~meta'].id);

          // First -> Align left to the label
          const iconOffset = arrowGroup ? 0 : size / 2;
          if (idx === 0) {
            yLabel = y;
            if (isNegative) {
              x -= dataLabelWidth / 2 + iconOffset;
              xWidth += dataLabelWidth / 2 + iconOffset;
            } else {
              xWidth -= dataLabelWidth / 2 + iconOffset;
            }
          }
          // Last -> Align right to the label
          if (idx === groupRecord[aggregation].length - 1) {
            yLabel = y;
            if (isNegative) {
              x += dataLabelWidth / 2 + iconOffset;
              xWidth -= dataLabelWidth / 2 + iconOffset;
            } else {
              xWidth += dataLabelWidth / 2 + iconOffset;
            }
          }
        } else {
          // In case only one dot per lane, align away from zero
          yLabel = y;
          xWidth += dataLabelWidth / 2 + size / 2;
          if (isNegative) x -= dataLabelWidth / 2 + size / 2;
        }
      }

      // Store Info
      if (!labelData[`${aggregation}`]) labelData[`${aggregation}`] = {};
      labelData[`${aggregation}`][`${obj.key}`] = {
        id: actualObj['~meta'].id,
        x,
        y,
        width: dataLabelWidth,
        height,
        value,
      };

      const renderConfig: any = dataLabel.getRenderConfig({
        mode: 'horizontal',
        animation,
        actualSize,
        htmlStr: dataLabelStr,
        value,
        availableWidth,
        availableHeight: 0,
        textRotate: chartManager.textRotate,
        color: '#fff', // Always black text
        backgroundColor,
        x,
        y: yLabel,
        width: xWidth,
        height,
        labelWidth: dataLabelWidth,
        labelHeight: dataLabelHeight,
        chartWidth: chartManager.chartWidth,
        chartHeight: chartManager.chartHeight,
        zeroX: chartManager.xScale(0),
        zeroY: chartManager.yScale(0),
        dataLabelOffset: chartManager.dataLabelOffset,
        callbacks: callbacksQ,
        valuePosition: 'inside', // values.position,
        positionAltInside: 'stack', // values.positionAltInside
        positionAltOutside: values.positionAltOutside,
      });

      // if (renderConfig) renderConfig.outline = '1px solid #f335ff' // DEBUG

      if (renderConfig) overlayData.push(renderConfig);
    }

    if (dots.ranges && isArray(dots.ranges)) {
      for (const range of dots.ranges) {
        if ([range.from, range.to].includes('*')) continue;
        if (range.from === range.to) continue;
                        
        const aggregationColor = colorMap[stack.values[0].actualObj[coloringField] as string]        

        let objFrom: any;
        let objTo: any;
        if (range.from === '__zero') {
          objFrom = { actualObj: { '~meta': null } };
          colorMap.__zero = sameColor ? aggregationColor : colorMap[range.to];
        } else {
          objFrom = stack.values.find((item: any) => item.key === range.from);
        }

        if (range.to === '__zero') {
          objTo = { actualObj: { '~meta': null } };
          colorMap.__zero = sameColor ? aggregationColor : colorMap[range.from];
        } else {
          objTo = stack.values.find((item: any) => item.key === range.to);
        }

        if (!objFrom) continue;
        if (!objTo) continue;

        const from: number = range.from === '__zero' ? 0 : stack[range.from];
        const to: number = range.to === '__zero' ? 0 : stack[range.to];
        const aggregation = stack['~meta'].group;

        if (from === to) continue;

        const labelWidthFrom = objFrom.actualObj['~meta']?.dataLabelWidth || 0;
        const labelWidthTo = objTo.actualObj['~meta']?.dataLabelWidth || 0;

        const labelHeightUpdated = objFrom.actualObj['~meta']?.labelHeightUpdated || 0;
        const labelHeight = objTo.actualObj['~meta']?.labelHeight || 0;

        const { colorMode, stroke, strokeWidth, strokeDasharray, directional } = range;
        const gradientStops: GradientStopData[] = [];

        let x1: number;
        let y1: number;
        let x2: number;
        let y2: number;

        const { item } = chartManager.yScaleOrdinal(aggregation);
        const y = item.y;
        const height = item.height ?? chartManager.dotLaneHeight;

        y1 = y + height / 2;
        y2 = y + height / 2;

        x1 = chartManager.xScale(from);
        x2 = chartManager.xScale(to);

        gradientStops[0] = { offset: '0%', color: colorMap[range.from] };
        gradientStops[1] = { offset: '100%', color: colorMap[range.to] };

        let _stroke = '#000';
        switch (colorMode) {
          case 'source':
            _stroke = sameColor ? aggregationColor : colorMap[range.from];
            break;
          case 'target':
            _stroke = sameColor ? aggregationColor : colorMap[range.to];
            break;
          case 'gradient':
            _stroke = sameColor ? aggregationColor : `url(#gradient-${range.id}-${stackIdx})`;
            break;
          case 'custom':
            _stroke = stroke;
            break;
        }

        if (isNumber(x1) && isNumber(y1) && isNumber(x2) && isNumber(y2)) {
          const rangeAnimate = {
            enabled: animation.enabled,
            delay: animation.delay,
            duration: animation.duration,
            x1,
            y1,
            x2: range.directional ? (to < from ? x2 + 5 : x2 - 5) : x2, // 5 offset for arrow
            y2,
          };

          // callbacks
          const clickQ = (event, _, node) => {};
          const callbacksQ = { click: clickQ };

          const x0 = chartManager.xScale(0);
          rangeData.push({
            x1: x0,
            y1,
            x2: x0,
            y2,
            color: _stroke,
            width: strokeWidth,
            lineDash: strokeDasharrayFormat(strokeDasharray),
            animate: rangeAnimate,
            callbacks: callbacksQ,
          });

          if (colorMode === 'gradient') {
            gradientData.push({
              id: `gradient-${range.id}-${stackIdx}`,
              x1,
              y1,
              x2,
              y2,
              stops: gradientStops,
            });
          }
        }

        // Extra Labels
        if (dots.extraLabels.enabled) {
          // const diff = labelData[aggregation][range.to].value - labelData[aggregation][range.from].value
          const diff = to - from;

          const obj: any = {};
          obj[aggregationField] = aggregation;
          obj[labelField] = range.to;
          let labelValue = 0;

          switch (dots.extraLabels.mode) {
            case 'diff':
              labelValue = diff;
              break;
            case 'change': {
              const change = ((to - from) / (Math.abs(from) || 1)) * 100;
              labelValue = change;
              break;
            }
            default: // none
              break;
          }
          obj[valueField] = labelValue;

          // Original Data label
          const anchor = labelData[aggregation][range.to];

          // console.log("🚀 ~ extraLabelsStyle:", dots.extraLabelsStyle)
          // console.log("🚀 ~ dots.extraLabelsQuill:", JSON.parse(dots.extraLabelsQuill))
          const extraLabelsStyle = cloneObject(dots.extraLabels.style);
          extraLabelsStyle.size = chartManager.getDynamicFontSize(dots.extraLabels.style);
          const extraLabelsQuill = chartManager.getHTMLandRect(dots.extraLabels.quill, extraLabelsStyle, obj, chartManager.content, true);
          const availableWidth = chartManager.horizontalSpace(obj[valueField]);

          // const labelWidth = labelValue > 0 ?
          // const rangeAdjust = chartManager.rangeMin >= 0 ? Math.min(chartManager.rangeMin, to) : Math.min(0, to);
          let x = chartManager.xScale(to > 0 ? 0 : to);
          let width = chartManager.horizontalWidth(to);
          let y = anchor.y;

          // Label Position
          const customDots = dots.customDots.find((item: CustomDots) => item.id === obj.key);
          const size = customDots ? customDots.size : dots.size;
          let extraOffset = 0;
          if (dots.extraLabels.position === 'away') {
            // Adjust offset
            extraOffset = ['spread-top', 'spread-bottom'].includes(dots.labelPosition) ? anchor.width : 0;
            const iconOffset = directional ? 0 : size / 2;
            // Adjust polarity
            if (to >= 0) {
              if (labelValue >= 0) width += extraLabelsQuill.width + iconOffset + extraOffset;
              if (labelValue < 0) width -= iconOffset + extraOffset;
            } else {
              if (labelValue >= 0) {
                width -= iconOffset + extraOffset;
                x += iconOffset + extraOffset;
              }
              if (labelValue < 0) {
                width += extraLabelsQuill.width + iconOffset + extraOffset;
                x -= extraLabelsQuill.width + iconOffset + extraOffset;
              }
            }
          }
          if (['top', 'bottom'].includes(dots.extraLabels.position)) {
            // Adjust offset
            extraOffset = dots.extraLabels.position === dots.labelPosition ? anchor.width : 0;
            width += extraLabelsQuill.width / 2 + extraOffset;
            if (to < 0) x -= extraLabelsQuill.width / 2 + extraOffset;
          }

          if (dots.extraLabels.position === 'top') y -= size / 2 + extraLabelsQuill.height / 2;
          if (dots.extraLabels.position === 'bottom') y += size / 2 + extraLabelsQuill.height / 2;

          const extraLabelConfig: any = dataLabel.getRenderConfig({
            mode: 'horizontal',
            animation,
            actualSize: undefined,
            htmlStr: extraLabelsQuill.html,
            ignoreColor: true,
            value: obj[valueField],
            availableWidth,
            availableHeight: 0,
            textRotate: chartManager.textRotate,
            color: '#fff', // Always black text
            backgroundColor,
            x,
            y,
            width,
            height: anchor.height,
            labelWidth: extraLabelsQuill.width,
            labelHeight: extraLabelsQuill.height,
            chartWidth: chartManager.chartWidth,
            chartHeight: chartManager.chartHeight,
            zeroX: chartManager.xScale(0),
            zeroY: chartManager.yScale(0),
            dataLabelOffset: chartManager.dataLabelOffset,
            callbacks: {},
            valuePosition: 'inside', // values.position,
            positionAltInside: 'stack', // values.positionAltInside
            positionAltOutside: values.positionAltOutside,
          });

          if (extraLabelConfig) {
            // Fix orientation
            if (to < 0) extraLabelConfig.justifyContent = 'flex-start';
            if (to > 0) extraLabelConfig.justifyContent = 'flex-end';
            // extraLabelConfig.outline = '1px solid #d97706' // DEBUG

            overlayData.push(extraLabelConfig);
          }
        }
      }
    }
  }

  // First labels
  if (dots.firstLabel.enabled) {
    const { grid, axis } = typeSpecific.chart;
    const labels = chartManager.dotFirstLabelsHTML.slice(0, 2); // take up to two 
    const maxValue = Math.max(...labels.map((label) => label.value));
    for (const firstLabel of chartManager.dotFirstLabelsHTML) {
      const xScaled = chartManager.xScale(firstLabel.value);
      // TODO: customize postion
      const x = firstLabel.value < maxValue ? xScaled - firstLabel.width : xScaled;
      const firstLabelData: any = {
        html: firstLabel.html,
        x,
        y: 0,
        width: firstLabel.width,
        height: firstLabel.height,
        color: firstLabel.color,
        flex: '1 1 auto',
        callbacks: {},
        justifyContent: 'flex-start',
        alignItems: 'center',
        transform: `translate(${x}px, ${0}px)`,
        animate: {
          enabled: true,
          delay: 20,
          duration: 600,
          transform: `translate(${x}px, ${0}px)`,
        },
      };
      // firstLabel.outline = '1px solid #d97706' // DEBUG
      overlayData.push(firstLabelData);

      // lines
      const { item, label } = chartManager.yScaleOrdinal(firstLabel.aggregation);
      const laneHeight = Math.max(item.height, label.height);

      const {line} = dots.firstLabel

      if (line.enabled) {        
        rangeData.push({
          x1: xScaled,
          y1: firstLabel.height,
          x2: xScaled,
          y2: label.y + laneHeight / 2,
          color: line.stroke,
          width: line.strokeWidth,
          lineDash: strokeDasharrayFormat(line.strokeDasharray),
          animation: {
            enabled: animation.enabled,
            delay: animation.delay,
            duration: animation.duration,
            x1: xScaled,
            y1: firstLabel.height,
            x2: xScaled,
            y2: label.y + laneHeight / 2,
          },
        });
      }
    }
  }

  // clear
  d3renderRect(chartManager.chart.element, [], '.chart');
  d3renderPath(chartManager.chart.element, [], '.chart');
  d3renderCircle(chartManager.chart.element, [], '.chart');
  d3renderGradient(chartManager.chart.element, [], '.chart');
  d3renderPathFilled(chartManager.chart.element, [], '.voronoi');
  d3renderText(chartManager.chart.element, [], '.text');

  d3renderLine(chartManager.chart.element, rangeData, '.chart');
  d3renderPathFilled(chartManager.chart.element, data, '.chart', chartManager.animationStop);
  d3renderGradient(chartManager.chart.element, gradientData, '.chart');
  // d3renderText(chartManager.chart, textData, '.text');

  overlayText(chartManager.overlay, overlayData, '.overlay-values');
};
